import React, { useEffect, useRef } from "react";

import { Box, Typography } from "@mui/material";

import { QRCodeCanvas } from "qrcode.react";

import {
  CARD_ASPECT_RATIO_CSS,
  CARD_BORDER_RADIUS,
  scaledCardPx,
} from "@/shared/constants/cardLayout";

import LimitedEditionCardBorder from "../borders/LimitedEditionCardBorder";
import { toPng } from "html-to-image";

/* -------------------------------------------------------------------------- */
/* Props                                                                      */
/* -------------------------------------------------------------------------- */

type DeckCardLimitedEditionFrontFaceProps = {
  compact?: boolean;
  size?: number;
  darkMode?: boolean;
  talentName?: string;
  title?: string;
  imageUrl?: string;
  editionNumber?: number | string | null;
  cardType?: string;
  totalEdition?: string;
  verificationUrl?: string;
  purchaseId?: number | string | null;
  addBorder?: boolean;
  downloadTrigger?: number;
  onDownloadComplete?: () => void;
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const DeckCardlimitedEditionFrontFaceupdated: React.FC<DeckCardLimitedEditionFrontFaceProps> = ({
  compact = false,
  size,
  darkMode = false,
  talentName = "HANRORO",
  title = "사랑하게 될꺼야",
  imageUrl,
  editionNumber,
  cardType = "SINGLE ALBUM CARD",
  totalEdition,
  purchaseId,
  verificationUrl,
  addBorder = false,
  downloadTrigger = 0,
  onDownloadComplete,
}) => {
  // Ref points at the card DOM node we want to turn into an image
  const cardRef = useRef<HTMLDivElement>(null);

  // Keep the latest complete callback without re-running the download effect
  const onDownloadCompleteRef = useRef(onDownloadComplete);
  onDownloadCompleteRef.current = onDownloadComplete;

  // When downloadTrigger changes, save the card as a PNG
  useEffect(() => {
    // 0 means the user has not clicked Download yet
    if (!downloadTrigger) {
      return;
    }

    let cancelled = false;

    const saveCardImage = async () => {
      // Wait 2 frames so the card is fully painted before capture
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });

      const cardElement = cardRef.current;

      if (!cardElement || cancelled) {
        onDownloadCompleteRef.current?.();
        return;
      }

      // If the card still has no size, wait a little longer
      if (cardElement.offsetWidth === 0 || cardElement.offsetHeight === 0) {
        await new Promise((resolve) => setTimeout(resolve, 120));
      }

      if (!cardElement.offsetWidth || !cardElement.offsetHeight || cancelled) {
        console.error("Could not download card image: card has no size");
        onDownloadCompleteRef.current?.();
        return;
      }

      try {
        // Turn the card HTML into a PNG image
        const dataUrl = await toPng(cardElement, {
          cacheBust: true,
          pixelRatio: 2,
          width: cardElement.scrollWidth || cardElement.offsetWidth,
          height: cardElement.scrollHeight || cardElement.offsetHeight,
          backgroundColor: "#090909",
        });

        if (cancelled) {
          return;
        }

        // Create a temporary link and click it to download
        const link = document.createElement("a");
        link.download = `${title || "limited-edition-card"}.png`;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Could not download card image", error);
      } finally {
        if (!cancelled) {
          onDownloadCompleteRef.current?.();
        }
      }
    };

    void saveCardImage();

    return () => {
      cancelled = true;
    };
  }, [downloadTrigger, title]);

  /* ------------------------------------------------------------------------ */
  /* Card sizing                                                              */
  /* ------------------------------------------------------------------------ */

  const cardRadius = compact
    ? {
        xs: "12px",
        sm: "14px",
        md: "16px",
      }
    : {
        xs: "14px",
        sm: "16px",
        md: "18px",
      };

  const cardPaddingX = compact
    ? "4.5%"
    : {
        xs: 1.75,
        sm: 1.75,
        md: 1.75,
      };

  const cardPaddingTop = compact
    ? "5%"
    : {
        xs: 1.75,
        sm: 2.1,
        md: 2.4,
      };

  const cardPaddingBottom = compact
    ? "5%"
    : {
        xs: 1.6,
        sm: 1.9,
        md: 2.2,
      };

  const sectionGap = compact
    ? "clamp(5px, 2.75cqw, 8px)"
    : {
        xs: 1.15,
        sm: 1.35,
        md: 1.55,
      };

  const typeGap = compact
    ? "clamp(3px, 1.75cqw, 5px)"
    : {
        xs: 0.7,
        sm: 0.85,
        md: 1,
      };

  const talentNameSize = compact
    ? "clamp(0.56rem, 3.5cqw, 0.74rem)"
    : {
        xs: "0.82rem",
        sm: "0.96rem",
        md: "1.08rem",
      };

  const titleSize = compact
    ? "clamp(0.8rem, 5.4cqw, 1.08rem)"
    : {
        xs: "1.2rem",
        sm: "1.5rem",
        md: "1.72rem",
      };

  const cardTypeSize = compact
    ? "clamp(0.52rem, 3.4cqw, 0.68rem)"
    : {
        xs: "0.55rem",
        sm: "0.64rem",
        md: "0.72rem",
      };

  const editionSize = compact
    ? "clamp(0.5rem, 3.1cqw, 0.62rem)"
    : {
        xs: "0.64rem",
        sm: "0.72rem",
        md: "0.8rem",
      };

  const limitedBadgeSize = {
    xs: "0.52rem",
    sm: "0.58rem",
    md: "0.64rem",
  };

  const limitedBadgeIconSize = {
    xs: "9px",
    sm: "10px",
    md: "11px",
  };

  /* ------------------------------------------------------------------------ */
  /* Purchase verification identity                                          */
  /* ------------------------------------------------------------------------ */

  const hasPurchaseId =
    purchaseId !== undefined &&
    purchaseId !== null &&
    String(purchaseId).trim() !== "" &&
    Number(purchaseId) > 0;

  /* ------------------------------------------------------------------------ */
  /* Edition                                                                  */
  /* ------------------------------------------------------------------------ */

  const hasEditionNumber =
    editionNumber !== undefined && editionNumber !== null && String(editionNumber).trim() !== "";

  const formattedEdition = hasEditionNumber ? String(editionNumber).padStart(3, "0") : null;

  /* ------------------------------------------------------------------------ */
  /* QR visibility                                                            */
  /* ------------------------------------------------------------------------ */

  const canShowQr = addBorder && purchaseId;

  /* ------------------------------------------------------------------------ */
  /* Application origin                                                       */
  /* ------------------------------------------------------------------------ */
  const appOrigin = typeof window !== "undefined" ? window.location.origin.replace(/\/+$/, "") : "";

  /* ------------------------------------------------------------------------ */
  /* Verification URL                                                         */
  /* ------------------------------------------------------------------------ */

  const verificationPath = hasPurchaseId
    ? `/limited-edition/verify/${encodeURIComponent(String(purchaseId))}?limitedEdition=${true}`
    : "";

  const qrValue = canShowQr ? verificationUrl?.trim() || `${appOrigin}${verificationPath}` : "";

  /* ------------------------------------------------------------------------ */
  /* QR debug                                                                 */
  /* ------------------------------------------------------------------------ */

  const qrDisplaySize = compact ? "clamp(32px, 16cqw, 44px)" : "70px";

  /* ------------------------------------------------------------------------ */
  /* Front face                                                               */
  /* ------------------------------------------------------------------------ */

  const FrontFace = () => {
    return (
      <Box
        sx={{
          position: "relative",

          width: "100%",

          containerType: "inline-size",
          border: addBorder ? "none" : "1px solid var(--primary-600)",

          aspectRatio: CARD_ASPECT_RATIO_CSS,

          flexShrink: 0,

          overflow: "hidden",

          borderRadius: cardRadius,

          boxSizing: "border-box",

          bgcolor: darkMode ? "#050505" : "#090909",

          isolation: "isolate",

          display: "flex",

          flexDirection: "column",

          px: cardPaddingX,

          pt: cardPaddingTop,

          pb: cardPaddingBottom,

          color: "#fff",

          boxShadow: compact ? "0 8px 24px rgba(0,0,0,0.3)" : "0 16px 40px rgba(0,0,0,0.4)",
        }}
      >
        {/* ================================================================ */}
        {/* Header                                                           */}
        {/* ================================================================ */}

        <Box
          sx={{
            display: "flex",

            alignItems: "center",

            justifyContent: "space-between",

            mb: sectionGap,

            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: talentNameSize,

              fontWeight: 700,

              letterSpacing: compact ? "0.11em" : "0.14em",

              textTransform: "uppercase",

              lineHeight: 1,

              color: "#fff",

              textShadow: "0 1px 3px rgba(0,0,0,0.45)",

              whiteSpace: "nowrap",

              overflow: "hidden",

              textOverflow: "ellipsis",

              maxWidth: "78%",
            }}
          >
            {talentName}
          </Typography>

          {/* Brand mark */}

          {addBorder && (
            <Typography
              component="span"
              aria-hidden
              sx={{
                color: "#ff8700",

                fontSize: compact
                  ? "clamp(1rem, 6.5cqw, 1.3rem)"
                  : {
                      xs: "1.35rem",
                      sm: "1.7rem",
                    },

                fontWeight: 900,

                lineHeight: 0.8,

                textShadow: "0 0 7px rgba(255,135,0,0.45)",

                flexShrink: 0,
              }}
            >
              <img
                src="https://assets-dev.taasnet.com/medias/IWBJDA73Q0-WhatsApp_Image_2026-08-21_at_9.06.34_AM__1_-removebg-preview1.webp"
                alt="Brand Mark"
                width={22}
                height={22}
              />
            </Typography>
          )}
        </Box>

        {/* ================================================================ */}
        {/* Main artwork                                                     */}
        {/* ================================================================ */}

        <Box
          sx={{
            position: "relative",

            width: "100%",

            flex: 1,

            minHeight: 0,

            overflow: "hidden",

            borderRadius: compact
              ? "clamp(8px, 4.5cqw, 12px)"
              : {
                  xs: "12px",
                  sm: "14px",
                  md: "16px",
                },

            bgcolor: "#171717",

            /**
             * This border belongs to
             * the artwork frame.
             *
             * Keep it even when
             * addBorder=true.
             */
            border: "1px solid rgba(255,255,255,0.62)",

            boxShadow: `
              inset 0 0 0 1px rgba(0,0,0,0.35),
              0 5px 15px rgba(0,0,0,0.3)
            `,
          }}
        >
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt={title}
              draggable={false}
              sx={{
                display: "block",

                width: "100%",

                height: "100%",

                objectFit: "cover",

                userSelect: "none",

                transition: "transform 0.35s ease",

                ".MuiBox-root:hover &": {
                  transform: compact ? "none" : "scale(1.015)",
                },
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",

                height: "100%",

                display: "grid",

                placeItems: "center",

                background: "linear-gradient(145deg, #171717, #0d0d0d)",

                color: "rgba(255,255,255,0.35)",
              }}
            >
              <Typography
                sx={{
                  fontSize: compact ? "clamp(0.5rem, 3.2cqw, 0.65rem)" : "0.75rem",

                  letterSpacing: "0.08em",
                }}
              >
                NO ARTWORK
              </Typography>
            </Box>
          )}

          {/* Image vignette */}

          <Box
            aria-hidden
            sx={{
              position: "absolute",

              inset: 0,

              pointerEvents: "none",

              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.04), transparent 55%, rgba(0,0,0,0.08))",
            }}
          />
        </Box>

        {/* ================================================================ */}
        {/* Bottom information                                               */}
        {/* ================================================================ */}

        <Box
          sx={{
            mt: compact
              ? "clamp(6px, 3.2cqw, 10px)"
              : {
                  xs: 1.15,
                  sm: 1.35,
                  md: 1.55,
                },

            display: "grid",

            /**
             * purchaseId exists:
             *
             * information | QR
             *
             * purchaseId absent:
             *
             * information
             */
            gridTemplateColumns: canShowQr ? "minmax(0, 1fr) auto" : "minmax(0, 1fr)",

            columnGap: canShowQr ? (compact ? 0.65 : 1.15) : 0,

            alignItems: "end",

            flexShrink: 0,
          }}
        >
          {/* ============================================================ */}
          {/* Left information                                            */}
          {/* ============================================================ */}

          <Box
            sx={{
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: compact ? 0.5 : 0.7,
              alignItems: "flex-start",
            }}
          >
            {/* Special limited edition badge */}
            {addBorder && (
              <Box
                aria-label="Special limited edition"
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: compact ? "4px" : "6px",
                  maxWidth: "100%",
                  px: compact
                    ? "clamp(6px, 3.2cqw, 9px)"
                    : {
                        xs: 1,
                        sm: 1.15,
                        md: 1.3,
                      },
                  py: compact
                    ? "clamp(2px, 1.2cqw, 3.5px)"
                    : {
                        xs: 0.3,
                        sm: 0.38,
                        md: 0.45,
                      },
                  borderRadius: compact ? "5px" : "7px",
                  overflow: "hidden",
                  background: `
                  linear-gradient(
                    118deg,
                    rgba(255, 214, 140, 0.28) 0%,
                    rgba(255, 135, 0, 0.18) 42%,
                    rgba(120, 55, 0, 0.35) 100%
                  )
                `,
                  border: "1px solid rgba(255, 196, 110, 0.72)",
                  boxShadow: `
                  0 0 10px rgba(255, 135, 0, 0.22),
                  inset 0 1px 0 rgba(255, 255, 255, 0.22),
                  inset 0 -1px 0 rgba(0, 0, 0, 0.35)
                `,
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(105deg, transparent 18%, rgba(255,255,255,0.28) 46%, transparent 62%)",
                    pointerEvents: "none",
                  },
                }}
              >
                <Box
                  component="svg"
                  aria-hidden
                  viewBox="0 0 12 12"
                  sx={{
                    width: limitedBadgeIconSize,

                    height: limitedBadgeIconSize,

                    flexShrink: 0,

                    filter: "drop-shadow(0 0 3px rgba(255, 200, 100, 0.55))",
                  }}
                >
                  <path
                    d="M6 0.75L7.35 4.65L11.25 6L7.35 7.35L6 11.25L4.65 7.35L0.75 6L4.65 4.65L6 0.75Z"
                    fill="#ffd27a"
                  />
                </Box>

                <Typography
                  sx={{
                    position: "relative",
                    color: "#ffe3ad",
                    fontSize: limitedBadgeSize,
                    fontWeight: 800,
                    letterSpacing: compact ? "0.09em" : "0.12em",
                    lineHeight: 1,
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textShadow: "0 1px 2px rgba(0,0,0,0.55)",
                  }}
                >
                  Special Limited Edition
                </Typography>
              </Box>
            )}

            {/* Card title */}

            <Typography
              title={title}
              sx={{
                width: "100%",

                fontFamily: '"Nanum Pen Script", "Noto Sans KR", sans-serif',

                fontSize: titleSize,

                fontWeight: 500,

                lineHeight: 1.4,

                color: "#fff",

                whiteSpace: "nowrap",

                overflow: "hidden",

                textOverflow: "ellipsis",

                textShadow: "0 2px 5px rgba(0,0,0,0.45)",
              }}
            >
              {title}
            </Typography>

            {/* Card type */}

            <Box
              sx={{
                mt: compact
                  ? "clamp(4px, 2.4cqw, 7px)"
                  : {
                      xs: 0.7,
                      sm: 0.85,
                      md: 1,
                    },

                display: "inline-flex",

                alignItems: "center",

                width: "fit-content",

                maxWidth: "100%",

                px: compact
                  ? "clamp(6px, 3.5cqw, 9px)"
                  : {
                      xs: 1,
                      sm: 1.2,
                      md: 1.35,
                    },

                py: compact
                  ? "clamp(2px, 1.4cqw, 4px)"
                  : {
                      xs: 0.35,
                      sm: 0.42,
                      md: 0.5,
                    },

                borderRadius: "999px",

                border: "1px solid #ff8700",

                background: "rgba(5,5,5,0.6)",

                boxShadow: "0 0 7px rgba(255,135,0,0.08)",
              }}
            >
              <Typography
                sx={{
                  color: "#fff",

                  fontSize: cardTypeSize,

                  fontWeight: 700,

                  letterSpacing: "0.07em",

                  lineHeight: 1,

                  whiteSpace: "nowrap",

                  overflow: "hidden",

                  textOverflow: "ellipsis",
                }}
              >
                {cardType}
              </Typography>
            </Box>

            {/* ========================================================== */}
            {/* Edition                                                   */}
            {/* ========================================================== */}

            {hasEditionNumber && formattedEdition && addBorder && (
              <Typography
                sx={{
                  mt: compact ? 0.45 : 0.75,

                  color: "rgba(255,255,255,0.78)",

                  fontSize: editionSize,

                  fontWeight: 500,

                  letterSpacing: "0.04em",

                  lineHeight: 1,
                }}
              >
                {Number(formattedEdition) === Number(totalEdition)
                  ? "Exhausted"
                  : `# ${formattedEdition} / ${totalEdition}`}{" "}
              </Typography>
            )}
          </Box>

          {/* ============================================================ */}
          {/* QR                                                          */}
          {/* ============================================================ */}

          {canShowQr && qrValue && (
            <Box
              title={`Verify purchase ${purchaseId}`}
              sx={{
                position: "relative",

                display: "flex",

                alignItems: "center",

                justifyContent: "center",

                p: compact ? "4px" : "6px",

                borderRadius: compact ? "7px" : "11px",

                background: "linear-gradient(145deg, #111 0%, #171717 100%)",

                border: "1px solid #ff8700",

                boxShadow: `
                    0 0 7px rgba(255,135,0,0.32),
                    inset 0 0 0 1px rgba(255,255,255,0.04)
                  `,

                lineHeight: 0,

                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: qrDisplaySize,

                  aspectRatio: "1 / 1",

                  background: "#fff",

                  borderRadius: compact ? "2px" : "3px",

                  overflow: "hidden",

                  lineHeight: 0,

                  "& canvas": {
                    display: "block",

                    width: "100% !important",

                    height: "100% !important",
                  },
                }}
              >
                <QRCodeCanvas
                  value={qrValue}
                  size={256}
                  bgColor="#000000"
                  fgColor="#ffffff"
                  level="M"
                  includeMargin={false}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  /* ------------------------------------------------------------------------ */
  /* Border wrapper                                                           */
  /* ------------------------------------------------------------------------ */

  return (
    <Box
      ref={cardRef}
      sx={{
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
      }}
    >
      {addBorder ? (
        <Box
          sx={{
            position: "relative",

            width: "100%",

            height: "100%",

            borderRadius: scaledCardPx(CARD_BORDER_RADIUS, 1),

            /**
             * Do not clip effects produced
             * by LimitedEditionCardBorder.
             */
            overflow: "visible",

            boxSizing: "border-box",
          }}
        >
          <LimitedEditionCardBorder>
            <FrontFace />
          </LimitedEditionCardBorder>
        </Box>
      ) : (
        <FrontFace />
      )}
    </Box>
  );
};

export default DeckCardlimitedEditionFrontFaceupdated;
